import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { ChevronDownIcon, EyeSlashIcon, PauseIcon, PlayIcon, PrinterIcon, QueueListIcon, StopIcon } from "@heroicons/react/24/outline";
import useJobAssemblyTimers from "../../hooks/useJobAssemblyTimers";
import usePartAssembly from "../../data/usePartAssembly";
import useFlowTypeAccounts from "../../data/useFlowTypeAccounts";
import useSingleSetting from "../../data/useSingleSetting";
import { useAuth } from "../../contexts/AuthContext";
import { IPartAssembly } from "../../models/Part";
import { AccountType } from "../../models/account/IAccount";
import { JobPartCategory } from "../../models/Job";
import JobPart from "./JobPart";
import Modal from "../../layouts/Modal";
import printDismantlingPlan, {
  buildJobAssemblyGroups,
  DismantlingPlanHeader,
} from "./printDismantlingPlan";
import LoadingWheel from "../shared/LoadingWheel";
import PinProtect, { PinProtectState } from "../shared/PinProtect";
import formatDateTime from "../../utils/formatDateTime";
import classNames from "../../utils/classNames";

// Component to handle indeterminate checkbox state
function IndeterminateCheckbox({
  checked,
  indeterminate,
  onChange,
  className
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  className?: string;
}) {
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      type="checkbox"
      ref={checkboxRef}
      checked={checked}
      onChange={onChange}
      className={className}
    />
  );
}

export const HIDDEN_ASSEMBLIES_STORAGE_KEY = "jobPartsHiddenAssemblies";
const NON_ASSEMBLY_PARTS_ID = "00000000-0000-0000-0000-000000000000";

export const readHiddenAssemblyIds = (): string[] => {
  try {
    const saved = localStorage.getItem(HIDDEN_ASSEMBLIES_STORAGE_KEY);
    if (!saved) return [];

    const parsed = JSON.parse(saved);
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === "string")
      : [];
  } catch {
    return [];
  }
};

export interface JobAssemblyWithParts extends IPartAssembly {
  jobParts: JobPartCategory[];
}

export interface JobPartsByAssemblyProps {
  jobId?: string;
  jobPartCategories: JobPartCategory[];
  markAsRemoved: (partData: JobPartCategory['partData']) => void;
  setDamaged: (partData: JobPartCategory['partData']) => void;
  setDamagedOpen: (open: boolean) => void;
  setMissing: (partData: JobPartCategory['partData']) => void;
  setMissingOpen: (open: boolean) => void;
  markNotAccessible: (partData: JobPartCategory['partData']) => void;
  handlePrint: (partData: JobPartCategory['partData']) => void;
  selectedPartIds: Set<string>;
  togglePartSelection: (partId: string | null | undefined) => void;
  toggleAssemblySelection: (assemblyParts: JobPartCategory[]) => void;
  onDiagramClick?: (partCategory: JobPartCategory) => void;
  /**
   * When set, only the assembly with this id is shown (used by Station Manager
   * to focus a single station). The hidden-assembly controls and the
   * Non-Assembly parts section are suppressed in this mode.
   */
  restrictToAssemblyId?: string;
  /**
   * When true (Station Manager), parts assigned to a Recycling account are
   * removed from the visible parts grid. They stay part of the underlying job
   * (so timers and the scan "mark the rest as removed" flow still cover them)
   * and are viewable via a per-assembly list icon that opens a simplified view.
   */
  hideRecyclingParts?: boolean;
  /**
   * Station Manager's dense layout: a single-row assembly header with the
   * timer and Start / Pause / Finish inline, and compact part cards that fit
   * four across when the sidebar is collapsed.
   */
  compact?: boolean;
  /** Vehicle/job details printed at the top of a per-assembly dismantling plan. */
  printHeader?: DismantlingPlanHeader;
  /**
   * Intercepts the Finish button. Return true to take over (the caller shows its own
   * confirmation and finishes from there); false lets the button finish the timer as normal.
   */
  onFinishRequest?: (assembly: IPartAssembly) => boolean;
}

export default function JobPartsByAssembly({
  jobId,
  jobPartCategories,
  markAsRemoved,
  setDamaged,
  setDamagedOpen,
  setMissing,
  setMissingOpen,
  markNotAccessible,
  handlePrint,
  selectedPartIds,
  togglePartSelection,
  toggleAssemblySelection,
  onDiagramClick,
  restrictToAssemblyId,
  hideRecyclingParts = false,
  compact = false,
  printHeader,
  onFinishRequest,
}: JobPartsByAssemblyProps) {
  const { t } = useTranslation("jobs");
  const {
    sessionRows,
    isAssemblyActionLoading,
    handleAssemblyStart,
    handleAssemblyPause,
    handleAssemblyFinish,
  } = useJobAssemblyTimers(jobId);
  const { assemblies } = usePartAssembly();
  const { flowTypeAccounts } = useFlowTypeAccounts("JobPartsByAssembly");
  const { userId } = useAuth();
  const { singleSetting } = useSingleSetting("IgnorePartPin");

  // When the IgnorePartPin setting is enabled we skip the PIN modal entirely
  // and act as the currently logged-in user.
  const ignorePartPin = useMemo(() => {
    return singleSetting.isLoading ? false : singleSetting.data?.data?.value === "true";
  }, [singleSetting.isLoading, singleSetting.data?.data?.value]);

  // Account ids that route parts to recycling. Parts on these accounts are
  // hidden from the grid when hideRecyclingParts is set (Station Manager).
  const recyclingAccountIds = useMemo(() => {
    const ids = new Set<string>();
    (flowTypeAccounts.data?.data ?? []).forEach((account) => {
      if (account.accountType === AccountType.Recycling) ids.add(account.id);
    });
    return ids;
  }, [flowTypeAccounts.data]);

  const isRecyclingPart = (jobPart: JobPartCategory) => {
    const accountId = jobPart.partData?.accountId;
    return !!accountId && recyclingAccountIds.has(accountId);
  };

  // Split an assembly's parts into those shown in the grid and those hidden as
  // recycling. When hideRecyclingParts is off everything is shown.
  const splitRecyclingParts = (parts: JobPartCategory[]) => {
    if (!hideRecyclingParts) return { shown: parts, recycling: [] as JobPartCategory[] };
    const shown: JobPartCategory[] = [];
    const recycling: JobPartCategory[] = [];
    parts.forEach((part) => (isRecyclingPart(part) ? recycling : shown).push(part));
    return { shown, recycling };
  };

  const [isLoading, setIsLoading] = useState(true);
  const [assemblyGroups, setAssemblyGroups] = useState<JobAssemblyWithParts[]>([]);
  const [unmatchedParts, setUnmatchedParts] = useState<JobPartCategory[]>([]);
  const [now, setNow] = useState<number>(Date.now());
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinState, setPinState] = useState<PinProtectState>();
  // The simplified "hidden recycling parts" view: title + the parts to list.
  const [recyclingView, setRecyclingView] = useState<{ title: string; parts: JobPartCategory[] } | null>(null);
  const [collapsedAssemblies, setCollapsedAssemblies] = useState<Record<string, boolean>>({});
  const [hiddenAssemblyIds, setHiddenAssemblyIds] = useState<string[]>(readHiddenAssemblyIds);
  const [pendingCollapseAssemblyId, setPendingCollapseAssemblyId] = useState<string | null>(null);
  const hasInitialCollapseStateLoadedRef = useRef(false);
  const previousCompletionStateRef = useRef<Record<string, boolean>>({});
  const previousActiveSessionStateRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const formatDuration = (seconds: number) => {
    const safeSeconds = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(safeSeconds / 3600).toString().padStart(2, "0");
    const minutes = Math.floor((safeSeconds % 3600) / 60).toString().padStart(2, "0");
    const remainingSeconds = Math.floor(safeSeconds % 60).toString().padStart(2, "0");

    return `${hours}:${minutes}:${remainingSeconds}`;
  };

  const parseApiUtcDate = (value: string) => DateTime.fromISO(value, { zone: "utc" });
  // A missing partData means the part was deleted (e.g. the vehicle was crushed) —
  // treat it as completed so the assembly doesn't read permanently incomplete.
  const isPartCompleted = (part: JobPartCategory) =>
    !part.partData ||
    !!part.partData.removedOn ||
    !!part.partData.isDamaged ||
    !!part.partData.isMissing ||
    !!part.partData.isNotAccessible;
  const isAssemblyCompleted = (assembly: JobAssemblyWithParts) =>
    assembly.jobParts.length > 0 && assembly.jobParts.every(isPartCompleted);

  useEffect(() => {
    if (!assemblies.isLoading && jobPartCategories) {
      // Keep assemblies with no parts on this job: they still get their header,
      // timer controls and a "no parts" message where the grid would be.
      const { groups, unmatched } = buildJobAssemblyGroups(
        assemblies.data?.data,
        jobPartCategories,
        true
      );
      setAssemblyGroups(groups);
      setUnmatchedParts(unmatched);
      setIsLoading(false);
    }
  }, [assemblies.isLoading, assemblies.data, jobPartCategories]);

  useEffect(() => {
    if (!assemblyGroups.length) return;

    const completionByAssembly: Record<string, boolean> = {};
    const activeSessionByAssembly: Record<string, boolean> = {};

    assemblyGroups.forEach((assembly) => {
      completionByAssembly[assembly.id] = isAssemblyCompleted(assembly);
      activeSessionByAssembly[assembly.id] = sessionRows.some(
        (session) => session.partAssemblyId === assembly.id && !session.dismantlingEnd
      );
    });

    setCollapsedAssemblies((prev) => {
      const next = { ...prev };

      if (!hasInitialCollapseStateLoadedRef.current) {
        assemblyGroups.forEach((assembly) => {
          if (next[assembly.id] === undefined) {
            next[assembly.id] = completionByAssembly[assembly.id];
          }
        });
        hasInitialCollapseStateLoadedRef.current = true;
      } else {
        assemblyGroups.forEach((assembly) => {
          const wasCompleted = previousCompletionStateRef.current[assembly.id] ?? false;
          const isCompleted = completionByAssembly[assembly.id];
          const wasActiveSession = previousActiveSessionStateRef.current[assembly.id] ?? false;
          const hasActiveSession = activeSessionByAssembly[assembly.id];

          if (!wasCompleted && isCompleted && !hasActiveSession) {
            next[assembly.id] = true;
          } else if (wasActiveSession && !hasActiveSession && isCompleted) {
            next[assembly.id] = true;
          } else if (next[assembly.id] === undefined) {
            next[assembly.id] = isCompleted;
          }
        });
      }

      return next;
    });

    previousCompletionStateRef.current = completionByAssembly;
    previousActiveSessionStateRef.current = activeSessionByAssembly;
  }, [assemblyGroups, sessionRows]);

  useEffect(() => {
    if (!pendingCollapseAssemblyId) return;

    const targetAssembly = assemblyGroups.find((assembly) => assembly.id === pendingCollapseAssemblyId);
    if (!targetAssembly) {
      setPendingCollapseAssemblyId(null);
      return;
    }

    // Wait until the stop action has actually settled (timer no longer active)
    // before deciding whether to collapse. Don't clear the pending id while the
    // session is still active, otherwise the refetch from finish/pause never
    // gets a chance to trigger the collapse.
    const hasActiveSession = sessionRows.some(
      (session) => session.partAssemblyId === pendingCollapseAssemblyId && !session.dismantlingEnd
    );

    if (hasActiveSession) return;

    if (isAssemblyCompleted(targetAssembly)) {
      setCollapsedAssemblies((prev) => ({
        ...prev,
        [pendingCollapseAssemblyId]: true,
      }));
    }

    setPendingCollapseAssemblyId(null);
  }, [pendingCollapseAssemblyId, assemblyGroups, sessionRows]);

  if (isLoading) {
    return <LoadingWheel />;
  }

  // Opens the PIN modal for a protected action, unless the IgnorePartPin
  // setting is on — in which case we run the success callback straight away
  // as the current user.
  const requestPin = (state: PinProtectState) => {
    if (ignorePartPin && userId) {
      state.onPinSuccess(userId);
      return;
    }
    setPinModalOpen(true);
    setPinState(state);
  };

  const openPausePin = (assemblyId: string, assemblyName: string) => {
    requestPin({
      pinLabel: t("Pause {{name}}", { name: assemblyName }),
      onPinSuccess: (pinUserId) => {
        if (!pinUserId) return;
        setPendingCollapseAssemblyId(assemblyId);
        handleAssemblyPause(assemblyId, "Paused", pinUserId);
      },
      onPinFailed: () => {
      },
    });
  };

  const hideAssembly = (assemblyId: string) => {
    setHiddenAssemblyIds((prev) => {
      if (prev.includes(assemblyId)) return prev;

      const next = [...prev, assemblyId];
      localStorage.setItem(HIDDEN_ASSEMBLIES_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const showAllHiddenAssemblies = () => {
    setHiddenAssemblyIds([]);
    localStorage.setItem(HIDDEN_ASSEMBLIES_STORAGE_KEY, JSON.stringify([]));
  };

  // Station Manager pins the view to a single assembly; everywhere else shows
  // every assembly the user hasn't hidden. Assemblies with no parts on this job
  // are included either way so their timer is still there to run.
  const visibleAssemblyGroups = restrictToAssemblyId
    ? assemblyGroups.filter((assembly) => assembly.id === restrictToAssemblyId)
    : assemblyGroups.filter((assembly) => !hiddenAssemblyIds.includes(assembly.id));

  const openFinishPin = (assemblyId: string, assemblyName: string) => {
    requestPin({
      pinLabel: t("Finish {{name}}", { name: assemblyName }),
      onPinSuccess: (pinUserId) => {
        if (!pinUserId) return;
        setPendingCollapseAssemblyId(assemblyId);
        handleAssemblyFinish(assemblyId, pinUserId);
      },
      onPinFailed: () => {
      },
    });
  };

  return (
    <>
      <PinProtect open={pinModalOpen} setOpen={setPinModalOpen} pinState={pinState} useAuthUser={false} />
      <Modal open={!!recyclingView} setOpen={(value) => setRecyclingView((prev) => (value ? prev : null))} width="max-w-lg">
        <div className="space-y-3">
          <div>
            <h1 className="text-lg font-semibold text-gray-800">{t("Recycling Parts")}</h1>
            <p className="text-sm text-gray-500">
              {t("{{name}} — hidden from the station view", { name: recyclingView?.title ?? "" })}
            </p>
          </div>
          <hr />
          <ul className="max-h-96 divide-y divide-gray-100 overflow-y-auto">
            {(recyclingView?.parts ?? []).map((jobPart) => {
              const part = jobPart.partData;
              const done = !!part?.removedOn || !!part?.isDamaged || !!part?.isMissing || !!part?.isNotAccessible;
              return (
                <li key={jobPart.partDataId ?? jobPart.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                  <span className="text-gray-700">
                    {part?.category?.name ?? jobPart.partCategory?.name ?? t("Part")}
                    {part?.tagNumber ? ` - ${part.tagNumber}` : ""}
                  </span>
                  {done && (
                    <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                      {part?.isDamaged
                        ? t("Damaged")
                        : part?.isMissing
                          ? t("Missing")
                          : part?.isNotAccessible
                            ? t("Not Accessible")
                            : t("Removed")}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </Modal>
      <div className="space-y-6">
      {!restrictToAssemblyId && hiddenAssemblyIds.length > 0 && (
        <button
          type="button"
          onClick={showAllHiddenAssemblies}
          className="rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-gray-300 hover:bg-gray-50"
        >
          {hiddenAssemblyIds.length === 1
            ? t("Show {{count}} hidden assembly", { count: hiddenAssemblyIds.length })
            : t("Show {{count}} hidden assemblies", { count: hiddenAssemblyIds.length })}
        </button>
      )}
      {/* Assembly Groups */}
      {visibleAssemblyGroups.map((assembly) => {
        const sessionsForAssembly = sessionRows.filter((session) => session.partAssemblyId === assembly.id);
        const activeSession = sessionsForAssembly.find((session) => !session.dismantlingEnd);
        const totalAssemblySeconds = sessionsForAssembly.reduce((total, session) => {
          if (!session.dismantlingStart) return total;

          const start = parseApiUtcDate(session.dismantlingStart);
          const end = session.dismantlingEnd
            ? parseApiUtcDate(session.dismantlingEnd)
            : DateTime.utc();

          const diff = Math.floor(end.diff(start, "seconds").seconds);
          return total + (diff > 0 ? diff : 0);
        }, 0);
        const sortedByActivity = [...sessionsForAssembly].sort((a, b) => {
          const aActivity = a.dismantlingEnd ?? a.dismantlingStart ?? a.created;
          const bActivity = b.dismantlingEnd ?? b.dismantlingStart ?? b.created;
          return parseApiUtcDate(bActivity).toMillis() - parseApiUtcDate(aActivity).toMillis();
        });
        const latestActivity = sortedByActivity[0];

        // Recycling parts stay in assembly.jobParts (so timers/completion and the
        // scan bulk-remove still cover them) but are pulled out of the grid.
        const { shown: displayParts, recycling: recyclingParts } = splitRecyclingParts(assembly.jobParts);
        const assemblyPartIds = displayParts
          .map(jobPart => jobPart.partData?.id)
          .filter((id): id is string => id !== null && id !== undefined);
        const allSelected = assemblyPartIds.length > 0 && assemblyPartIds.every(id => selectedPartIds.has(id));
        const someSelected = assemblyPartIds.some(id => selectedPartIds.has(id));
        const isCollapsed = collapsedAssemblies[assembly.id] ?? false;
        const isCompleted = isAssemblyCompleted(assembly);
        const remainingCount = displayParts.filter((part) => !isPartCompleted(part)).length;

        // Per-assembly icon toolbar (print plan, recycling list, hide, collapse).
        const iconToolbar = (
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={() => printDismantlingPlan([{ name: assembly.name, jobParts: assembly.jobParts }], printHeader, flowTypeAccounts.data?.data ?? [])}
              aria-label={t("Print dismantling plan for {{name}}", { name: assembly.name })}
              className="rounded-md bg-white p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
            >
              <PrinterIcon className="h-5 w-5" />
            </button>
            {recyclingParts.length > 0 && (
              <button
                type="button"
                onClick={() => setRecyclingView({ title: assembly.name, parts: recyclingParts })}
                aria-label={t("View {{count}} recycling parts for {{name}}", { count: recyclingParts.length, name: assembly.name })}
                className="relative rounded-md bg-white p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
              >
                <QueueListIcon className="h-5 w-5" />
                <span className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-gray-500 px-1 text-[10px] font-semibold text-white">
                  {recyclingParts.length}
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={() => hideAssembly(assembly.id)}
              aria-label={t("Hide {{name}}", { name: assembly.name })}
              className="rounded-md bg-white p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
            >
              <EyeSlashIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() =>
                setCollapsedAssemblies((prev) => ({
                  ...prev,
                  [assembly.id]: !isCollapsed,
                }))
              }
              aria-label={isCollapsed ? t("Expand {{name}}", { name: assembly.name }) : t("Collapse {{name}}", { name: assembly.name })}
              className="rounded-md bg-white p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
            >
              <ChevronDownIcon
                className={`h-5 w-5 transition-transform duration-200 ease-in-out ${isCollapsed ? "rotate-180" : ""}`}
              />
            </button>
          </div>
        );

        // Start, or Pause + Finish while a session is running. The compact
        // header uses bigger, icon-led buttons so they're obvious at a glance.
        const timerButtons = !activeSession ? (
          <button
            type="button"
            className={classNames(
              "flex items-center gap-x-1 rounded-md text-white disabled:cursor-not-allowed disabled:opacity-60",
              compact ? "bg-green-600 px-3 py-1.5 text-sm font-semibold hover:bg-green-700" : "bg-primary-600 px-3 py-1 hover:bg-primary-700"
            )}
            onClick={() => handleAssemblyStart(assembly.id)}
            disabled={isAssemblyActionLoading}
          >
            {compact && <PlayIcon className="h-4 w-4" />}
            {t("Start")}
          </button>
        ) : (
          <>
            <button
              type="button"
              className={classNames(
                "flex items-center gap-x-1 rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60",
                compact ? "px-3 py-1.5 text-sm font-semibold" : "px-3 py-1"
              )}
              onClick={() => openPausePin(assembly.id, assembly.name)}
              disabled={isAssemblyActionLoading}
            >
              {compact && <PauseIcon className="h-4 w-4" />}
              {t("Pause")}
            </button>
            <button
              type="button"
              className={classNames(
                "flex items-center gap-x-1 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60",
                compact ? "px-3 py-1.5 text-sm font-semibold" : "px-3 py-1"
              )}
              onClick={() => {
                if (onFinishRequest?.(assembly)) return;
                openFinishPin(assembly.id, assembly.name);
              }}
              disabled={isAssemblyActionLoading}
            >
              {compact && <StopIcon className="h-4 w-4" />}
              {t("Finish")}
            </button>
          </>
        );

        return (
          <div key={assembly.id} className={classNames("rounded-lg bg-gray-100 p-4", compact ? "@container" : "")}>
            {compact ? (
              // One row: name, vehicle, remaining, then the timer and its
              // controls on the right so the operator sees state at a glance.
              <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2">
                <IndeterminateCheckbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={() => toggleAssemblySelection(displayParts)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <h3 className="text-lg font-semibold">{assembly.name}</h3>
                {(printHeader?.vrm || printHeader?.makeModel) && (
                  <span className="rounded-md bg-white px-2 py-0.5 text-xs font-medium text-gray-700">
                    {[printHeader.vrm, printHeader.makeModel].filter(Boolean).join(" · ")}
                  </span>
                )}
                {isCompleted ? (
                  <span className="rounded-md bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">{t("Completed")}</span>
                ) : (
                  <span className="rounded-md bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                    {t("{{count}} remaining", { count: remainingCount })}
                  </span>
                )}
                {someSelected && (
                  <span className="text-xs text-gray-600">
                    {t("({{selected}} of {{total}} selected)", { selected: assemblyPartIds.filter(id => selectedPartIds.has(id)).length, total: assemblyPartIds.length })}
                  </span>
                )}
                <div className="ml-auto flex items-center gap-x-2">
                  <span className={classNames("font-mono text-lg font-semibold tabular-nums", activeSession ? "text-gray-900" : "text-gray-500")}>
                    {formatDuration(totalAssemblySeconds)}
                  </span>
                  {timerButtons}
                  {iconToolbar}
                </div>
              </div>
            ) : (
            <div className="flex flex-col gap-y-2 mb-4">
              <div className="flex items-center gap-2">
              <IndeterminateCheckbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={() => toggleAssemblySelection(displayParts)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <h3 className="text-lg font-semibold">
                {assembly.name}
                {isCompleted && <span className="ml-1 text-base text-green-700">- {t("Completed")}</span>}
              </h3>
              {someSelected && (
                <span className="text-sm text-gray-600">
                  {t("({{selected}} of {{total}} selected)", { selected: assemblyPartIds.filter(id => selectedPartIds.has(id)).length, total: assemblyPartIds.length })}
                </span>
              )}
              {iconToolbar}
              </div>

              <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
                <span className="rounded-full bg-white px-2 py-1 font-medium text-gray-700">
                  {t("Total:")} {formatDuration(totalAssemblySeconds)}
                </span>
                {activeSession && (
                  <span className="rounded-full bg-green-100 px-2 py-1 font-medium text-green-700">
                    {t("Active")}
                  </span>
                )}
                {latestActivity?.dismantlingStart && (
                  <span className="text-gray-600">
                    {t("Last activity:")} {formatDateTime(latestActivity.dismantlingEnd ?? latestActivity.dismantlingStart, DateTime.DATETIME_SHORT)}
                  </span>
                )}
                <div className="flex items-center gap-2">
                  {timerButtons}
                </div>
              </div>
            </div>
            )}
            {!isCollapsed && (
              displayParts.length > 0 ? (
              <div className={classNames(
                "grid gap-4",
                // Compact cards size to the assembly container rather than the
                // viewport, so collapsing the sidebar lets a fourth column in.
                // Steps keep each card at roughly 280px+ so the buttons and
                // QR both fit; four across arrives at ~1150px of content.
                compact
                  ? "grid-cols-1 @xl:grid-cols-2 @4xl:grid-cols-3 @6xl:grid-cols-4 gap-3"
                  : "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"
              )}>
                {displayParts.map((jobPart) => (
                  <JobPart
                    key={jobPart.partDataId}
                    partCategory={jobPart}
                    markAsRemoved={markAsRemoved}
                    setDamaged={setDamaged}
                    setDamagedOpen={setDamagedOpen}
                    setMissing={setMissing}
                    setMissingOpen={setMissingOpen}
                    markNotAccessible={markNotAccessible}
                    handlePrint={handlePrint}
                    selectedPartIds={selectedPartIds}
                    togglePartSelection={togglePartSelection}
                    onDiagramClick={onDiagramClick}
                    compact={compact}
                  />
                ))}
              </div>
              ) : (
                <p className="text-sm text-gray-500">
                  {recyclingParts.length > 0
                    ? t("All parts here are recycling — use the list icon above to view them.")
                    : restrictToAssemblyId
                      ? t("No parts set to this station")
                      : t("No parts to show.")}
                </p>
              )
            )}
          </div>
        );
      })}

      {/* Unmatched Parts */}
      {!restrictToAssemblyId && unmatchedParts.length > 0 && !hiddenAssemblyIds.includes(NON_ASSEMBLY_PARTS_ID) && (() => {
        const { shown: displayUnmatched, recycling: recyclingUnmatched } = splitRecyclingParts(unmatchedParts);
        const unmatchedPartIds = displayUnmatched
          .map(jobPart => jobPart.partData?.id)
          .filter((id): id is string => id !== null && id !== undefined);
        const allUnmatchedSelected = unmatchedPartIds.length > 0 && unmatchedPartIds.every(id => selectedPartIds.has(id));
        const someUnmatchedSelected = unmatchedPartIds.some(id => selectedPartIds.has(id));

        return (
          <div className="rounded-lg bg-gray-100 p-4">
            <div className="flex items-center gap-2 mb-4">
              <IndeterminateCheckbox
                checked={allUnmatchedSelected}
                indeterminate={someUnmatchedSelected && !allUnmatchedSelected}
                onChange={() => toggleAssemblySelection(displayUnmatched)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <h3 className="text-lg font-semibold">{t("Non-Assembly Parts")}</h3>
              {someUnmatchedSelected && (
                <span className="text-sm text-gray-600">
                  {t("({{selected}} of {{total}} selected)", { selected: unmatchedPartIds.filter(id => selectedPartIds.has(id)).length, total: unmatchedPartIds.length })}
                </span>
              )}
              <div className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => printDismantlingPlan([{ name: t("Non-Assembly Parts"), jobParts: unmatchedParts }], printHeader, flowTypeAccounts.data?.data ?? [])}
                  aria-label={t("Print dismantling plan for Non-Assembly Parts")}
                  className="rounded-md bg-white p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                >
                  <PrinterIcon className="h-5 w-5" />
                </button>
                {recyclingUnmatched.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setRecyclingView({ title: t("Non-Assembly Parts"), parts: recyclingUnmatched })}
                    aria-label={t("View {{count}} recycling parts for Non-Assembly Parts", { count: recyclingUnmatched.length })}
                    className="relative rounded-md bg-white p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                  >
                    <QueueListIcon className="h-5 w-5" />
                    <span className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-gray-500 px-1 text-[10px] font-semibold text-white">
                      {recyclingUnmatched.length}
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => hideAssembly(NON_ASSEMBLY_PARTS_ID)}
                  aria-label={t("Hide Non-Assembly Parts")}
                  className="rounded-md bg-white p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                >
                  <EyeSlashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            {displayUnmatched.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {displayUnmatched.map((jobPart) => (
                <JobPart
                  key={jobPart.id}
                  partCategory={jobPart}
                  markAsRemoved={markAsRemoved}
                  setDamaged={setDamaged}
                  setDamagedOpen={setDamagedOpen}
                  setMissing={setMissing}
                  setMissingOpen={setMissingOpen}
                  markNotAccessible={markNotAccessible}
                  handlePrint={handlePrint}
                  selectedPartIds={selectedPartIds}
                  togglePartSelection={togglePartSelection}
                  onDiagramClick={onDiagramClick}
                  compact={compact}
                />
              ))}
            </div>
            ) : (
              <p className="text-sm text-gray-500">
                {t("All parts here are recycling — use the list icon above to view them.")}
              </p>
            )}
          </div>
        );
      })()}
    </div>
    </>
  );
} 